import { FC, MouseEvent, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";

import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ActionsWrap, Td } from "@/components/Table";
import { ImageToolStyleDescriptionResource } from "@/types/resources";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import ImageToolSettingsModal from "./ImageToolSettingsModal";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";

interface Props {
  description: ImageToolStyleDescriptionResource;
}
const ImageToolSettingsTableRow: FC<Props> = ({ description }) => {
  const [showModal, setShowModal] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        "Are you sure you want to delete this description?"
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/image-generation-descriptions/${description.id}`
      );

      await queryClient.invalidateQueries(["image_generation_descriptions"], {
        exact: true,
      });

      alerts.success("Description was successfully deleted");
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <tr>
      <Td>{description.title}</Td>
      <Td>{description.description}</Td>
      <Td $actionsCol onClick={(e) => e.stopPropagation()}>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              as="button"
              onClick={() => setShowModal(true)}
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
      <AnimatePresence>
        {showModal && (
          <ImageToolSettingsModal
            description={description}
            onClose={() => setShowModal(false)}
          />
        )}
      </AnimatePresence>
    </tr>
  );
};

export default ImageToolSettingsTableRow;
